import React, { useState, useContext, useCallback } from 'react';
import RichText from '../components/RichText';
import { fetchAction, withAuthCheck, AuthError } from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useTranslation } from 'react-i18next';
import ItemViewDetails from '../components/ItemViewDetails';
import V3RuinsWidget from './widgets/V3RuinsWidget';
import V3OpsWidget from './widgets/V3OpsWidget';
import ItemIconV3 from './components/ItemIconV3';
import WarehouseDialog from './components/WarehouseDialog';
import WellDialog from './components/WellDialog';
import { v3Theme } from './theme';
import './V3RegionCard.css';

const V3RegionCard = ({ 
    // RegionCard props
    titleLabel,
    subtitle,
    imageFileName,
    peopleCount = 0,
    regionInfo = null,
    
    // LandWatch props
    landWatch = null,
    questReport = null,
    
    // Movement progress
    movementProgress = null,
    
    // Ruins and Operations widgets
    ruinsWidget = null,
    opsWidget = null,
    
    // Warehouse size info
    warehouseSize = null,
    
    // Available services (warehouse, well, etc)
    availableServices = [],
    
    // Narrative text for special locations
    narrative = null,
    
    // Collapse control
    isCollapsed: parentIsCollapsed,
    onToggleCollapse,
}) => {
    const token = useContext(AuthContext);
    const { showSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const [selectedItem, setSelectedItem] = useState(null);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(parentIsCollapsed !== undefined ? parentIsCollapsed : true);
    const [questProgress, setQuestProgress] = useState(0);
    const [warehouseDialogOpen, setWarehouseDialogOpen] = useState(false);
    const [wellDialogOpen, setWellDialogOpen] = useState(false);

    // Track render state
    
    // Track props changes
    React.useEffect(() => {
        // Props updated
    }, [opsWidget, landWatch, questReport, ruinsWidget, isCollapsed, parentIsCollapsed, movementProgress]);
    
    // Sync with parent collapsed state
    React.useEffect(() => {
        if (parentIsCollapsed !== undefined) {
            setIsCollapsed(parentIsCollapsed);
        }
    }, [parentIsCollapsed]);
    
    // Calculate quest progress (for both landWatch and movement)
    React.useEffect(() => {
        // Priority: landWatch progress > movement progress
        const progressData = landWatch?.questProgress || movementProgress;
        
        // Removed console.log for progress effect
        
        if (progressData) {
            const updateProgress = () => {
                const now = Date.now() / 1000; // Current time in seconds
                const { startTime, endTime } = progressData;
                
                // Don't use time offset - just use client time directly
                // The server and client are both using the same incorrect system time (2025)
                const adjustedNow = now;
                
                const duration = endTime - startTime;
                const elapsed = adjustedNow - startTime;
                const progress = Math.min(99, Math.max(0, (elapsed / duration) * 100));
                
                // Removed console.log for progress updates
                
                setQuestProgress(progress);
                
                // If we've reached 99%, the movement should complete soon
                // Log a warning if we're still showing progress after completion time
                if (progress >= 99 && adjustedNow > endTime + 15) {
                    console.warn('[V3RegionCard] Movement should have completed but still showing progress!', {
                        secondsPastEndTime: adjustedNow - endTime,
                        movementProgress,
                        subtitle
                    });
                }
            };
            
            updateProgress();
            const interval = setInterval(updateProgress, 1000); // Update every second
            
            return () => clearInterval(interval);
        } else {
            // No progress data, resetting to 0
            setQuestProgress(0);
        }
    }, [landWatch?.questProgress, movementProgress, subtitle]);
    
    
    const handleCollapseToggle = useCallback((e) => {
        e.stopPropagation();
        const newCollapsed = !isCollapsed;
        setIsCollapsed(newCollapsed);
        
        // Notify parent component about collapse state change
        if (onToggleCollapse) {
            onToggleCollapse(newCollapsed);
        }
    }, [isCollapsed, onToggleCollapse]);

    const handleButtonClick = (e) => {
        e.stopPropagation(); // Prevent card collapse
        
        if (!landWatch?.actionId) return;
        
        withAuthCheck(fetchAction, token, landWatch.actionId)
            .then(data => {
                showSnackbar(data.result, "success");
                
                // If the response includes updated widget data, apply it immediately
                // This provides instant UI feedback while SSE update is in flight
                if (data.widget && window.v3UpdateLandWatch) {
                    window.v3UpdateLandWatch(data.widget);
                }
            })
            .catch(err => {
                if (err instanceof AuthError) {
                    return;
                }
                const errorMessage = err.result || err.message || "error";
                showSnackbar(errorMessage, "error");
            });
    };
    
    const handlePeopleClick = (e) => {
        e.stopPropagation(); // Prevent card collapse
        showSnackbar(`${peopleCount} heroes in this region`, "info");
    };
    
    // Helper function to render a LandWatch-style section
    const renderLandWatchSection = (data, isQuestReport = false) => {
        if (!data) return null;
        
        return (
            <div className="v3-landwatch-section">
                {/* Title - only for quest report */}
                {isQuestReport && data.title && (
                    <div className="v3-quest-report-title">
                        <RichText content={
                            data.staminaValue > 0 
                                ? `${data.title} {stamina}${data.staminaValue}`
                                : data.title
                        } />
                    </div>
                )}
                
                {/* Content Text */}
                {data.contentText && (
                    <div className="v3-landwatch-content">
                        <RichText content={data.contentText} />
                    </div>
                )}
                
                {/* Rewards Section */}
                {(data.expValue > 0 || data.goldValue > 0) && (
                    <div className="v3-landwatch-rewards">
                        {data.expValue > 0 && (
                            <div className="v3-landwatch-reward">
                                <img src="/img/exp.webp" alt="EXP" />
                                <div className="v3-landwatch-reward-value">
                                    {data.expValue}
                                </div>
                            </div>
                        )}
                        
                        {data.goldValue > 0 && (
                            <div className="v3-landwatch-reward">
                                <img src="/img/coins.webp" alt="Gold" />
                                <div className="v3-landwatch-reward-value">
                                    {data.goldValue}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                
                {/* Loot Grid */}
                {data.loot?.length > 0 && (
                    <div className="v3-landwatch-loot-section">
                        <div className="v3-landwatch-loot-title">
                            <RichText content="Loot" />
                        </div>
                        <div className="v3-landwatch-loot-grid">
                            {data.loot.map((item) => (
                                <div 
                                    key={item.code} 
                                    className="v3-landwatch-loot-item"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedItem(item);
                                    }}
                                >
                                    <ItemIconV3
                                        icon={item.icon}
                                        size={48}
                                    />
                                    <div className="v3-landwatch-loot-qty">
                                        {item.qty}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Status Label - removed, now shown in top right quadrant */}

                {/* Action Button */}
                {data.actionId && data.buttonLabel && (
                    <button
                        className="v3-landwatch-button"
                        onClick={handleButtonClick}
                        disabled={data.buttonDisabled}
                        style={{
                            backgroundColor: data.buttonColor,
                            color: data.buttonTextColor,
                            opacity: data.buttonDisabled ? 0.6 : 1,
                            cursor: data.buttonDisabled ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {data.buttonLabel}
                    </button>
                )}
            </div>
        );
    };
    
    // Extract region info - fallback when server doesn't send structured data
    const extractRegionInfo = () => {
        // This is a fallback - server should send structured regionInfo
        // For now just return basic info
        const info = [];
        
        // Always show lands
        info.push({
            icon: 'shield',
            text: 'lands'
        });
        
        return info;
    };

    return (
        <div className={`v3-region-card ${isCollapsed ? 'collapsed' : ''}`}>
            <div className="v3-region-card-container">
                {/* Top Left - Region Image + People Count */}
                <div className="v3-region-image-section">
                    <div className="v3-region-image-wrapper">
                        {imageFileName && (
                            <img
                                className="v3-region-image"
                                src={`/img/map/${imageFileName}`}
                                alt="Region"
                                onLoad={() => setImageLoaded(true)}
                                onError={() => setImageLoaded(true)}
                            />
                        )}
                        
                        {(!imageFileName || !imageLoaded) && (
                            <div className="v3-region-image-skeleton" />
                        )}
                        
                        {!movementProgress && (
                            <button 
                                className="v3-region-people-button"
                                onClick={handlePeopleClick}
                            >
                                {peopleCount}
                                <svg className="v3-region-people-icon" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                                </svg>
                            </button>
                        )}
                    </div>
                </div>
                
                {/* Top Right - Title and LandWatch Status */}
                <div className="v3-region-info-section" onClick={handleCollapseToggle}>
                    <div className="v3-region-title">
                        <RichText content={titleLabel || '-'} />
                    </div>
                    <div className="v3-region-status-box">
                        {(landWatch?.questProgress || movementProgress) && (
                            <div 
                                className="v3-region-status-progress" 
                                style={{ 
                                    width: `${questProgress}%`,
                                    opacity: questProgress > 0 ? 1 : 0
                                }}
                            />
                        )}
                        <div className="v3-region-status-text">
                            <RichText content={
                                movementProgress 
                                    ? (subtitle?.includes('Traveling') ? `🐪 Traveling (${Math.round(questProgress)}%)` : `🥾 Moving (${Math.round(questProgress)}%)`) 
                                    : (landWatch?.statusLabel || 'Ready for Unity Call')
                            } />
                        </div>
                    </div>
                    {/* Collapse/Expand Icon */}
                    <div className="v3-region-collapse-button">
                        <svg className="v3-region-collapse-icon" viewBox="0 0 24 24" fill="currentColor">
                            {isCollapsed ? (
                                <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                            ) : (
                                <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6 1.41 1.41z"/>
                            )}
                        </svg>
                    </div>
                </div>
                
                {/* Region info - shown full width when expanded and narrative exists */}
                {!isCollapsed && narrative && (regionInfo || extractRegionInfo()).length > 0 && (
                    <div style={{ 
                        gridColumn: '1 / -1',
                        padding: '12px 16px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                    }}>
                        {(regionInfo || extractRegionInfo()).map((item, index) => (
                            <div key={index} className="v3-status-icon-row">
                                {item.icon && (
                                    <img 
                                        className="v3-status-icon" 
                                        src={`/img/${item.icon}.webp`} 
                                        alt={item.icon} 
                                    />
                                )}
                                <span className="v3-status-icon-text">{item.text}</span>
                            </div>
                        ))}
                    </div>
                )}
                
                {/* Narrative text - shown full width when expanded and narrative exists */}
                {!isCollapsed && narrative && (
                    <div style={{ 
                        gridColumn: '1 / -1',
                    }}>
                        <div style={{
                            margin: '0 3px',
                            padding: '16px',
                            backgroundColor: 'rgba(0, 0, 0, 0.03)',
                            fontSize: '0.9rem',
                            lineHeight: '1.5',
                            color: v3Theme.text.primary
                        }}>
                            <RichText content={narrative} />
                        </div>
                    </div>
                )}
                
                {/* Bottom Row - Status Icons and LandWatch Content */}
                <div className="v3-region-bottom-row">
                    {/* Bottom Left - Subtitle and region info */}
                    <div className="v3-region-status-icons">
                        {/* Show region info here only in collapsed state or when no narrative */}
                        {(isCollapsed || !narrative) && (regionInfo || extractRegionInfo()).map((item, index) => (
                            <div key={index} className="v3-status-icon-row">
                                {item.icon && (
                                    <img 
                                        className="v3-status-icon" 
                                        src={`/img/${item.icon}.webp`} 
                                        alt={item.icon} 
                                    />
                                )}
                                <span className="v3-status-icon-text">{item.text}</span>
                            </div>
                        ))}
                    </div>
                    
                    {/* Bottom Right - LandWatch Content */}
                    <div className="v3-region-landwatch-content">
                        {/* LandWatch Section */}
                        {renderLandWatchSection(landWatch, false)}
                        
                        {/* Warehouse button - show when expanded and warehouse is available */}
                        {!isCollapsed && availableServices.includes('warehouse') && (
                            <div className="v3-region-warehouse-section" style={{ 
                                marginBottom: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                gap: '12px'
                            }}>
                                <button
                                    className="v3-landwatch-button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setWarehouseDialogOpen(true);
                                    }}
                                    style={{
                                        backgroundColor: v3Theme.main,
                                        color: 'white',
                                        width: 'auto'
                                    }}
                                >
                                    Warehouse
                                </button>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <RichText content={`${warehouseSize?.currentSize || 0}{weight}`} />
                                </div>
                            </div>
                        )}
                        
                        {/* Well button - show when expanded and well is available */}
                        {!isCollapsed && availableServices.includes('well') && (
                            <div className="v3-region-well-section" style={{ 
                                marginBottom: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                gap: '12px'
                            }}>
                                <button
                                    className="v3-landwatch-button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setWellDialogOpen(true);
                                    }}
                                    style={{
                                        backgroundColor: v3Theme.main,
                                        color: 'white',
                                        width: 'auto'
                                    }}
                                >
                                    {t('buttons.throw')}
                                </button>
                            </div>
                        )}
                        
                        {/* Operations Widget - only show when expanded */}
                        {!isCollapsed && opsWidget && (
                            <div className="v3-region-ops-widget">
                                <V3OpsWidget
                                    key={opsWidget.actionId} // Force re-render when action changes
                                    iconName={opsWidget.iconName}
                                    text={opsWidget.text}
                                    buttonLabel={opsWidget.buttonLabel}
                                    buttonColor={opsWidget.buttonColor}
                                    textColor={opsWidget.textColor}
                                    actionId={opsWidget.actionId}
                                />
                            </div>
                        )}
                        
                        {/* Quest Report Section - Shows previous patrol summary, ALWAYS LAST */}
                        {questReport && renderLandWatchSection(questReport, true)}
                    </div>
                </div>
                
                {/* Ruins Widget - show full width outside of bottom row */}
                {!isCollapsed && ruinsWidget && (
                    <div style={{ 
                        gridColumn: '1 / -1',
                        padding: '0 16px 16px 16px'
                    }}>
                        <V3RuinsWidget
                            bosses={ruinsWidget.bosses}
                            location={ruinsWidget.location}
                        />
                    </div>
                )}
            </div>
            

            {/* Item Details Modal */}
            {selectedItem && (
                <ItemViewDetails
                    open={true}
                    onClose={() => setSelectedItem(null)}
                    item={selectedItem}
                />
            )}
            
            {/* Warehouse Dialog */}
            {warehouseDialogOpen && (
                <WarehouseDialog
                    open={warehouseDialogOpen}
                    onClose={() => setWarehouseDialogOpen(false)}
                />
            )}
            
            {/* Well Dialog */}
            {wellDialogOpen && (
                <WellDialog
                    open={wellDialogOpen}
                    onClose={() => setWellDialogOpen(false)}
                />
            )}
        </div>
    );
};

export default React.memo(V3RegionCard);